import Checklist from "@mui/icons-material/Checklist";
import Class from "@mui/icons-material/Class";
import Plagiarism from "@mui/icons-material/Plagiarism";
import Settings from "@mui/icons-material/Settings";
import Splitscreen from "@mui/icons-material/Splitscreen";
import Summarize from "@mui/icons-material/Summarize";
import Badge from "@mui/material/Badge";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import React, { useEffect } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  ClassificationConfig,
  ExtractionConfig,
  PipelineConfigDb,
  ValidationConfig,
} from "../../../interfaces/doc";
import { PipelineConfigForm } from "../../../schemas/OrionConfig";
import { TabPanel, getTabProps } from "../../Common/Tabs";
import DashboardLayout from "../../DashboardLayout/DashboardLayout";
import ConfigHeader from "../../PageHeader/ConfigHeader";
import WorkspacePromptingDialog from "../../useDialogs/WorkspacePromptingDialog";
import { useDialogs } from "../../useDialogs/useDialogs";
import { useNotifications } from "../../useNotifications/useNotifications";
import useSession from "../../useSession/useSession";
import { useCreateConfig, useUpdateConfig } from "../OrionQueries";
import ConfigClassification from "./ConfigClassification";
import ConfigExtraction from "./ConfigExtraction";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";
import ConfigGeneralite from "./ConfigGeneralite";
import ConfigJsonPanel from "./ConfigJsonPanel";
import ConfigNoClassification from "./ConfigNoClassification";
import ConfigNoExtraction from "./ConfigNoExtraction";
import ConfigNoSplit from "./ConfigNoSplit";
import ConfigNoSynthesis from "./ConfigNoSynthesis";
import ConfigNoValidation from "./ConfigNoValidation";
import ConfigSplit from "./ConfigSplit";
import ConfigSynthesis from "./ConfigSynthesis";
import {
  getClassificationQueries,
  getExtractionQueries,
  getValidationRules,
} from "./ConfigUtils";
import ConfigValidation from "./ConfigValidation";

interface ConfigFormProps {
  configDb: PipelineConfigDb | undefined;
}

const ConfigForm: React.FC<ConfigFormProps> = ({ configDb }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const navigate = useNavigate();
  const notifications = useNotifications();

  const {
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useFormConfigContext();
  const { isNew, isVisMode } = useConfigModeContext();
  const { data: newConfig, mutate: createConfig } = useCreateConfig();
  const { mutate: updateConfig } = useUpdateConfig();
  const dialogs = useDialogs();
  const session = useSession();

  const onSubmit: SubmitHandler<PipelineConfigForm> = (data) => {
    console.log("Submitted", data);
    try {
      if (isNew) {
        if ((session?.OrionContributorGroups ?? []).length > 1) {
          dialogs.open(WorkspacePromptingDialog, {
            workspaces: session?.OrionContributorGroups ?? [],
            onChose: (workspace_id: string | null) => {
              createConfig({
                name: data.name,
                workspace_id,
                config: data.config,
              });
            },
          });
        } else {
          createConfig({
            name: data.name,
            workspace_id: session!.OrionContributorGroups[0],
            config: data.config,
          });
        }
      } else {
        updateConfig({
          configId: configDb?._id as string,
          ...data,
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const onError = (data: any) => {
    console.log("Errors", data);
    console.log("Errored Form", watch());
    notifications.show("Veuillez vérifier la validité de votre configuration", {
      severity: "error",
      autoHideDuration: 3000,
    });
  };
  useEffect(() => {
    if (configDb) {
      reset({
        config: configDb.config,
        name: configDb.name,
      });
    }
  }, [configDb, reset]);

  useEffect(() => {
    if (!!newConfig) {
      navigate(`/orion/config/${newConfig._id}`);
    }
  }, [newConfig, navigate]);

  const { config } = watch();

  return (
    <DashboardLayout>
      <Container maxWidth={false} className="flex flex-col overflow-hidden">
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="overflow-hidden"
        >
          <Stack
            spacing={2}
            className="flex flex-col my-2 h-full overflow-hidden"
          >
            <ConfigHeader />
            {isVisMode ? (
              <div className="p-4 overflow-y-auto">
                <ConfigJsonPanel />
              </div>
            ) : (
              <>
                <Tabs
                  value={activeTab}
                  onChange={handleChange}
                  aria-label="Config Tabs"
                  variant="fullWidth"
                  className="mt-6 border-solid border-0 border-b border-black"
                >
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 0,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={
                          errors.config?.language ||
                          errors.name ||
                          errors.config?.workflow
                            ? "error"
                            : undefined
                        }
                      >
                        <Settings />
                        Général
                      </Typography>
                    }
                    {...getTabProps(0)}
                  />
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 1,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={errors.config?.split ? "error" : undefined}
                      >
                        <Badge
                          badgeContent={config?.split ? 1 : 0}
                          color={activeTab === 1 ? "primary" : undefined}
                        >
                          <Splitscreen />
                        </Badge>
                        Découpage
                      </Typography>
                    }
                    {...getTabProps(1)}
                  />
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 2,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={
                          errors.config?.classification ? "error" : undefined
                        }
                      >
                        <Badge
                          badgeContent={getClassificationQueries(
                            config?.classification as ClassificationConfig
                          )}
                          color={activeTab === 2 ? "primary" : undefined}
                        >
                          <Class />
                        </Badge>
                        Classification
                      </Typography>
                    }
                    {...getTabProps(2)}
                  />
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 3,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={errors.config?.extraction ? "error" : undefined}
                      >
                        <Badge
                          badgeContent={getExtractionQueries(
                            config?.extraction as ExtractionConfig
                          )}
                          color={activeTab === 3 ? "primary" : undefined}
                        >
                          <Plagiarism />
                        </Badge>
                        Extraction
                      </Typography>
                    }
                    {...getTabProps(3)}
                  />
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 4,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={errors.config?.validation ? "error" : undefined}
                      >
                        <Badge
                          badgeContent={getValidationRules(
                            config?.validation as ValidationConfig
                          )}
                          color={activeTab === 4 ? "primary" : undefined}
                        >
                          <Checklist />
                        </Badge>
                        Validation
                      </Typography>
                    }
                    {...getTabProps(4)}
                  />
                  <Tab
                    className={classNames({
                      "bg-slate-200 rounded-t-lg": activeTab === 5,
                    })}
                    label={
                      <Typography
                        className="flex gap-2 items-center"
                        color={errors.config?.synthesis ? "error" : undefined}
                      >
                        <Badge
                          badgeContent={config?.synthesis ? 1 : 0}
                          color={activeTab === 5 ? "primary" : undefined}
                        >
                          <Summarize />
                        </Badge>
                        Synthèse
                      </Typography>
                    }
                    {...getTabProps(5)}
                  />
                </Tabs>
                <TabPanel
                  index={0}
                  value={activeTab}
                  className={classNames("flex flex-col gap-4", {
                    "mt-0": activeTab !== 0,
                    "mt-6 flex flex-col gap-4 overflow-y-hidden":
                      activeTab === 0,
                  })}
                >
                  <ConfigGeneralite />
                </TabPanel>
                <TabPanel
                  index={1}
                  value={activeTab}
                  className={classNames({
                    "mt-0": activeTab !== 1,
                    "mt-6 flex flex-col gap-4": activeTab === 1,
                    "overflow-y-hidden": !!config?.split,
                  })}
                >
                  {config?.split ? <ConfigSplit /> : <ConfigNoSplit />}
                </TabPanel>
                <TabPanel
                  index={2}
                  value={activeTab}
                  className={classNames({
                    "mt-0": activeTab !== 2,
                    "mt-6 flex flex-col gap-4": activeTab === 2,
                    "overflow-y-hidden": !!config?.classification,
                  })}
                >
                  {config?.classification ? (
                    <ConfigClassification />
                  ) : (
                    <ConfigNoClassification />
                  )}
                </TabPanel>
                <TabPanel
                  index={3}
                  value={activeTab}
                  className={classNames({
                    "mt-0": activeTab !== 3,
                    "mt-6 flex flex-col gap-4": activeTab === 3,
                    "overflow-y-hidden": !!config?.extraction,
                  })}
                >
                  {config?.extraction ? (
                    <ConfigExtraction />
                  ) : (
                    <ConfigNoExtraction />
                  )}
                </TabPanel>
                <TabPanel
                  index={4}
                  value={activeTab}
                  className={classNames({
                    "mt-0": activeTab !== 4,
                    "mt-6 flex flex-col gap-4": activeTab === 4,
                    "overflow-y-hidden": !!config?.validation,
                  })}
                >
                  {config?.validation ? (
                    <ConfigValidation />
                  ) : (
                    <ConfigNoValidation />
                  )}
                </TabPanel>
                <TabPanel
                  index={5}
                  value={activeTab}
                  className={classNames({
                    "mt-0": activeTab !== 5,
                    "mt-6 flex flex-col gap-4": activeTab === 5,
                    "overflow-y-hidden": !!config?.synthesis,
                  })}
                >
                  {config?.synthesis ? (
                    <ConfigSynthesis />
                  ) : (
                    <ConfigNoSynthesis />
                  )}
                </TabPanel>
              </>
            )}
          </Stack>
        </form>
      </Container>
    </DashboardLayout>
  );
};
export default ConfigForm;
